import { OpenCode, type OpenCodeClient } from "@opencode/client"
import { CODENOMAD_API_BASE } from "./api-client"
import { backgroundReads } from "./background-read-queue"
import { authenticatedFetch } from "./auth-recovery"
import { prioritizedRead } from "./prioritized-read"
import { SESSION_ENVIRONMENT_FAILED_ERROR_CODE } from "../../../server/src/api-types"

class SDKManager {
  private clients = new Map<string, OpenCodeClient>()

  private key(instanceId: string, proxyPath: string): string {
    return `${instanceId}:${normalizeProxyPath(proxyPath)}`
  }

  createClient(instanceId: string, proxyPath: string, isForeground: () => boolean = () => false): OpenCodeClient {
    const key = this.key(instanceId, proxyPath)
    const existing = this.clients.get(key)
    if (existing) {
      return existing
    }

    const baseUrl = buildInstanceBaseUrl(proxyPath)
    const client = OpenCode.make({ baseUrl, fetch: createInstanceFetch(baseUrl, isForeground) })

    this.clients.set(key, client)

    return client
  }

  destroyClientsForInstance(instanceId: string): void {
    for (const key of Array.from(this.clients.keys())) {
      if (key === instanceId || key.startsWith(`${instanceId}:`)) {
        this.clients.delete(key)
      }
    }
  }
}

export type { OpenCodeClient }

export function buildInstanceBaseUrl(proxyPath: string, apiBase = CODENOMAD_API_BASE): string {
  const normalized = normalizeProxyPath(proxyPath)
  const base = stripTrailingSlashes(apiBase ?? "")
  return `${base}${normalized}/`
}

export function createInstanceFetch(baseUrl: string, isForeground: () => boolean = () => false): typeof globalThis.fetch {
  return (input, init) => {
    const requestUrl = new URL(input instanceof Request ? input.url : input)
    const basePath = new URL(baseUrl, requestUrl).pathname.replace(/\/+$/, "") + "/"
    // The pinned client preserves baseUrl's proxy prefix. Strip it only for
    // scheduling decisions; forwarding must retain the generated URL unchanged.
    const apiPath = requestUrl.pathname.startsWith(basePath)
      ? `/${requestUrl.pathname.slice(basePath.length)}` : requestUrl.pathname
    const read = async () => {
      const response = await authenticatedFetch(input, {
        ...init,
        credentials: init?.credentials ?? "include",
      })
      if (response.status === 426) {
        const { reportOpenCodeSetupRequired } = await import("../stores/opencode-setup")
        reportOpenCodeSetupRequired()
      }
      if (response.status === 502) {
        const body = await response.clone().json().catch(() => undefined)
        if (body?.error === SESSION_ENVIRONMENT_FAILED_ERROR_CODE) {
          const { tGlobal } = await import("./i18n")
          throw new Error(tGlobal("envEditor.applyFailed"))
        }
        if (body?.error === "PROVIDER_ACCOUNT_SELECTION_FAILED") {
          const { tGlobal } = await import("./i18n")
          throw new Error(tGlobal("settings.accounts.error"))
        }
      }
      return response
    }
    const method = init?.method ?? (input instanceof Request ? input.method : "GET")
    // Project identity gates the visible cross-worktree session list. Promote
    // this dependency with selection rather than leaving it behind Git scans.
    if (method === "GET" && /^\/api\/location\/?$/.test(apiPath)) {
      return prioritizedRead(isForeground, init?.signal ?? (input instanceof Request ? input.signal : new AbortController().signal), read)
    }
    // Catalogues from every restored project used to consume all HTTP/1.1
    // connections before the saved session/message reads could even dispatch.
    // Share the secondary budget with inventory scans, including reconnects.
    if (method === "GET" && /^\/api\/(?:project|location|agent(?:\/[^/]+)?|provider|model(?:\/default)?|command|shell|session\/active)\/?$/.test(apiPath)) {
      return backgroundReads.run(init?.signal ?? (input instanceof Request ? input.signal : new AbortController().signal), read)
    }
    return read()
  }
}

function normalizeProxyPath(proxyPath: string): string {
  const withLeading = proxyPath.startsWith("/") ? proxyPath : `/${proxyPath}`
  return withLeading.replace(/\/+/g, "/").replace(/\/+$/, "")
}

function stripTrailingSlashes(input: string): string {
  return input.replace(/\/+$/, "")
}

export const sdkManager = new SDKManager()
